import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { useAuth } from "./AuthContext";
import { useSpeech } from "../hooks/useSpeech";
import { isInstantStop, stripWake } from "../lib/voiceCommands";

const VoiceContext = createContext(null);

const COLOR_WORDS = {
  cyan: 185, bleu: 210, azur: 210, or: 43, doré: 43, jaune: 50, vert: 144,
  émeraude: 144, emeraude: 144, rouge: 345, cramoisi: 345, violet: 270,
  mauve: 275, rose: 320, orange: 25, turquoise: 175,
};

// Pages where the global mic engine must stay OFF: the Assistant page owns the mic
// there (big orb + rich flow), auth / payment / TV-cast are their own contexts.
function isExcludedPath(p) {
  if (p === "/" || p === "/auth" || p === "/tv") return true;
  return p.startsWith("/payment");
}

export function VoiceProvider({ children }) {
  const { user, updateSettings } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const speech = useSpeech();
  const {
    micReady, recogSupported, listening, speaking,
    micLevelRef, speakLevelRef,
    enableMic, startListening, stopListening, speak, stopSpeaking, unlockAudio,
  } = speech;

  const isAuthed = user && typeof user === "object";
  const excluded = isExcludedPath(pathname);

  const [globalActive, setGlobalActive] = useState(false);
  const [gBusy, setGBusy] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [subtitleRole, setSubtitleRole] = useState("assistant");

  const abortRef = useRef(null);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => {
    if (user && typeof user.tts_rate === "number") {
      localStorage.setItem("sara_tts_rate", String(user.tts_rate));
    }
  }, [user]);

  const saraState = speaking ? "speaking" : gBusy ? "thinking" : listening ? "listening" : "idle";

  const speakGlobal = useCallback((text) => {
    if (!text || !text.trim()) return;
    speak(text, userRef.current?.voice);
  }, [speak]);

  // ---- Voice bridge: iframes (e.g. trading-crypto price alerts) post {type:"SPEAK", text} ----
  useEffect(() => {
    const onMsg = (e) => {
      const d = e?.data;
      if (d && d.type === "SPEAK" && typeof d.text === "string" && d.text.trim()) {
        unlockAudio();
        speakGlobal(d.text);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [speakGlobal, unlockAudio]);

  // ---- Sara's <do>...</do> site actions (safe subset usable from any page) ----
  const runAction = useCallback((raw) => {
    const idx = raw.indexOf(":");
    const name = idx === -1 ? raw : raw.slice(0, idx);
    const arg = idx === -1 ? "" : raw.slice(idx + 1);
    switch (name) {
      case "set_color": {
        let hue = parseInt(arg, 10);
        if (Number.isNaN(hue)) hue = COLOR_WORDS[(arg || "").trim().toLowerCase()];
        if (typeof hue === "number" && !Number.isNaN(hue)) updateSettings({ hue }).catch(() => {});
        break;
      }
      case "scroll": {
        const a = (arg || "bottom").trim();
        window.scrollTo({ top: a === "top" ? 0 : document.body.scrollHeight, behavior: "smooth" });
        break;
      }
      case "open_url": {
        let u = (arg || "").trim();
        if (!u) break;
        if (!/^https?:\/\//i.test(u)) u = "https://" + u;
        window.open(u, "_blank", "noopener,noreferrer");
        break;
      }
      case "open_trading": navigate("/trading"); break;
      case "stop": stopSpeaking(); break;
      default: break;
    }
  }, [updateSettings, navigate, stopSpeaking]);

  // ---- hard kill-switch ("stop / arrête"): cut voice + in-flight LLM + backend actions ----
  const killAll = useCallback(() => {
    try { abortRef.current && abortRef.current.abort(); } catch (_) {}
    abortRef.current = null;
    stopSpeaking();
    setGBusy(false);
    setSubtitle("⏹ Arrêté");
    setSubtitleRole("assistant");
    api.post("/voice/stop-all").catch(() => {});
  }, [stopSpeaking]);

  const stopGlobalListeningRef = useRef(null);

  const simpleSend = useCallback(async (text) => {
    if (!text || !text.trim()) return;
    unlockAudio();
    stopSpeaking();
    setSubtitle(text);
    setSubtitleRole("user");
    setGBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const { data } = await api.post("/chat", { message: text }, { signal: controller.signal });
      abortRef.current = null;
      const actions = [...data.reply.matchAll(/<do>(.*?)<\/do>/gs)].map((m) => m[1].trim());
      const clean = data.reply.replace(/<do>.*?<\/do>/gs, "").trim();
      actions.forEach(runAction);
      setGBusy(false);
      setSubtitle(clean || "…");
      setSubtitleRole("assistant");
      speakGlobal(data.spoken_reply || clean);
      if (data.images?.length) toast.success("Image générée — visible sur la page Assistante");
      if (data.build_request?.prompt) toast("Projet lancé — ouvre le Studio pour le suivre");
    } catch (e) {
      abortRef.current = null;
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || api.isCancel?.(e)) {
        setGBusy(false);
        return;
      }
      setGBusy(false);
      setSubtitle("Erreur de liaison. Réessaie, je te prie.");
      setSubtitleRole("assistant");
    }
  }, [unlockAudio, stopSpeaking, runAction, speakGlobal]);

  // stable result handler (avoids restarting recognition on every render)
  const onResultRef = useRef(() => {});
  useEffect(() => {
    onResultRef.current = ({ final, interim: itm }) => {
      const u = userRef.current;
      if (itm && !final) { setSubtitle(itm); setSubtitleRole("user"); return; }
      if (!final || !final.trim()) return;
      let text = final.trim();
      if (u?.wake_word_enabled) {
        const stripped = stripWake(text, u?.wake_word);
        if (stripped == null) return;          // wake word absent → ignore
        if (!stripped) { speakGlobal("Oui ?"); return; }
        text = stripped;
      }
      if (isInstantStop(text)) { killAll(); return; }   // instant local command
      stopGlobalListeningRef.current && stopGlobalListeningRef.current();
      simpleSend(text);
    };
  }, [simpleSend, speakGlobal, killAll]);

  const startGlobalListening = useCallback(() => {
    startListening((r) => onResultRef.current && onResultRef.current(r));
  }, [startListening]);

  const stopGlobalListening = useCallback(() => {
    stopListening();
  }, [stopListening]);
  useEffect(() => { stopGlobalListeningRef.current = stopGlobalListening; }, [stopGlobalListening]);

  // ---- continuous global hands-free loop (every page except the excluded ones) ----
  const canGlobal = globalActive && isAuthed && !excluded;
  useEffect(() => {
    const shouldListen = canGlobal && micReady && recogSupported && !speaking && !gBusy;
    if (shouldListen) startGlobalListening();
    else stopGlobalListening();
  }, [canGlobal, micReady, recogSupported, speaking, gBusy, startGlobalListening, stopGlobalListening]);

  // ---- barge-in: operator talks over Sara while she's speaking → cut her off (desktop) ----
  useEffect(() => {
    if (!canGlobal || !speaking) return;
    let armed = false, hi = 0, raf;
    const check = () => {
      const lvl = micLevelRef.current || 0;
      if (!armed) { if (lvl < 0.12) armed = true; }
      else if (lvl > 0.3) { hi += 1; if (hi > 12) { killAll(); return; } }
      else hi = Math.max(0, hi - 1);
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    return () => cancelAnimationFrame(raf);
  }, [canGlobal, speaking, micLevelRef, killAll]);

  // reset when the operator logs out
  useEffect(() => {
    if (!isAuthed && globalActive) {
      setGlobalActive(false);
      stopGlobalListening();
      setSubtitle("");
    }
  }, [isAuthed, globalActive, stopGlobalListening]);

  const activateGlobal = useCallback(async () => {
    unlockAudio();
    const ok = await enableMic();
    if (!ok) { toast.error("Micro refusé par le navigateur — autorise-le pour Sara"); return false; }
    setGlobalActive(true);
    setSubtitle("À l'écoute…");
    setSubtitleRole("assistant");
    return true;
  }, [unlockAudio, enableMic]);

  const deactivateGlobal = useCallback(() => {
    setGlobalActive(false);
    stopGlobalListening();
    stopSpeaking();
    setSubtitle("");
  }, [stopGlobalListening, stopSpeaking]);

  const value = {
    micLevelRef, speakLevelRef,
    globalActive, activateGlobal, deactivateGlobal,
    subtitle, subtitleRole, saraState, listening, speaking, gBusy,
    recogSupported,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export const useVoice = () => useContext(VoiceContext);
